import { ArrowRight } from 'lucide-react'
import { useScrollReveal, useStaggerReveal } from '../../hooks/useScrollReveal.js'

/* ─── The familiar behaviour sequence ──────────────────────── */
const OLD_STEPS = [
  'You need something.',
  'You ask a friend.',
  'They say "let me check the group chat."',
  'A group suggests someone.',
  'That someone is unavailable.',
  'You start over.',
]

/* ─── Section ───────────────────────────────────────────────── */
export default function Problem() {
  const headerRef = useScrollReveal()
  const stepsRef = useStaggerReveal()
  const shiftRef = useScrollReveal({ threshold: 0.15 })

  return (
    <section className="py-16 md:py-24 bg-[#0E7C86] relative overflow-hidden">

      {/* Subtle texture */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
          backgroundSize: '36px 36px',
        }}
      />

      <div className="section-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

          {/* ── Left: the problem ───────────────────────────── */}
          <div ref={headerRef} className="reveal">
            <p className="eyebrow text-white/50 mb-4">
              <span className="w-5 h-px bg-white/40" />
              The Old Way
            </p>
            <h2
              className="font-extrabold text-white mb-6"
              style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.75rem)', lineHeight: 1.1 }}
            >
              Every problem starts<br className="hidden sm:block" /> the same way.
            </h2>
            <p className="text-base text-white/70 leading-relaxed max-w-sm">
              Across Ghana and across Africa, when you need something done,
              the first instinct is always the same: ask around, post in a
              group, wait for someone  <em className="text-white not-italic font-semibold">
                "who knows someone."</em>
            </p>
          </div>

          {/* ── Right: step-by-step sequence ─────────────────── */}
          <div ref={stepsRef} className="flex flex-col gap-3">
            {OLD_STEPS.map((step, i) => (
              <div
                key={step}
                className={`reveal reveal-delay-${i + 1} flex items-center gap-4
                            rounded-xl px-5 py-3.5
                            ${i === OLD_STEPS.length - 1
                    ? 'bg-white/5 border border-white/10'
                    : 'bg-white/8 border border-white/8'
                  }`}
              >
                <span
                  className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center
                               justify-center text-[11px] font-bold
                               ${i === OLD_STEPS.length - 1
                      ? 'bg-accent/20 text-accent'
                      : 'bg-white/15 text-white/60'
                    }`}
                >
                  {i + 1}
                </span>
                <p
                  className={`text-sm font-medium
                              ${i === OLD_STEPS.length - 1
                      ? 'text-accent font-bold'
                      : 'text-white/80'
                    }`}
                >
                  {step}
                </p>
              </div>
            ))}
          </div>

        </div>

        {/* ── Shift callout ─────────────────────────────────── */}
        <div
          ref={shiftRef}
          className="reveal mt-16 rounded-2xl bg-white p-8 md:p-10
                     flex flex-col md:flex-row md:items-center gap-6 md:gap-10"
        >
          {/* Old */}
          <div className="flex-1">
            <p className="text-[10px] font-bold tracking-[0.18em] uppercase
                          text-gray-400 mb-2">
              Old way
            </p>
            <p
              className="font-extrabold text-gray-300 line-through decoration-accent/50
                         decoration-[3px]"
              style={{ fontSize: 'clamp(1.2rem, 2.5vw, 1.75rem)', lineHeight: 1.15 }}
            >
              "Who knows someone?"
            </p>
          </div>

          {/* Arrow */}
          <div className="hidden md:flex items-center justify-center
                          w-10 h-10 rounded-full bg-primary-light flex-shrink-0">
            <ArrowRight className="w-5 h-5 text-primary" aria-hidden="true" />
          </div>

          {/* New */}
          <div className="flex-1">
            <p className="text-[10px] font-bold tracking-[0.18em] uppercase
                          text-primary mb-2">
              New way
            </p>
            <p
              className="font-extrabold text-brand-dark"
              style={{ fontSize: 'clamp(1.2rem, 2.5vw, 1.75rem)', lineHeight: 1.15 }}
            >
              Ask DecidA.{' '}
              <span className="text-primary">Get answers.</span>
            </p>
            <p className="text-sm text-brand-gray mt-2 leading-relaxed max-w-xs">
              Send one request. Get offers back in the app. Pick the best one.
              No groups. No waiting. No starting over.
            </p>
          </div>
        </div>

      </div>
    </section>
  )
}
